import React, { ButtonHTMLAttributes } from 'react'
import { Link, NavLinkProps } from 'react-router-dom'
import { SpinnerIcon } from './icons'
import { isNetworkRequestInFlight } from '@apollo/client/core/networkStatus'
import { LazyQueryResult, MutationResult, OperationVariables } from '@apollo/client'
import { ApolloStateButtonAttributes } from '../types/global'

const baseClassName = 'inline-flex items-center justify-center transition duration-150 ease-in-out focus:outline-none rounded disabled:opacity-50 disabled:cursor-not-allowed '

export type ButtonVariant = 'primary' | 'secondary' | 'danger'
type ButtonSize = 'sm' | 'md'

const variantClassNames: Record<ButtonVariant, string> = {
    primary: 'hover:bg-indigo-600 border bg-indigo-700 text-white ',
    secondary: 'hover:bg-indigo-50 border border-indigo-500 bg-white text-indigo-700 ',
    danger: 'hover:bg-red-50 border border-red-300 bg-white text-red-600 ',
}

const sizeClassNames: Record<ButtonSize, string> = {
    md: 'px-8 py-2 text-sm ',
    sm: 'px-3 py-1.5 text-xs ',
}

function composeClassName(variant: ButtonVariant, size: ButtonSize, extra?: string): string {
    return baseClassName + sizeClassNames[size] + variantClassNames[variant] + (extra || '')
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }

export function Button({ variant = 'secondary', size = 'md', className, children, ...rest }: ButtonProps): JSX.Element {
    return (
        <button {...rest} className={composeClassName(variant, size, className)}>
            {children}
        </button>
    )
}

export function PrimaryButton(props: ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
    return <Button variant='primary' {...props} />
}

export function PrimaryLinkButton(props: NavLinkProps): JSX.Element {
    return (
        <Link {...props} className={composeClassName('primary', 'md', props.className)}>
            {props.children}
        </Link>
    )
}

function resultLoading<D>(result: MutationResult<D> | LazyQueryResult<D, OperationVariables>): boolean {
    return 'networkStatus' in result
        ? isNetworkRequestInFlight((result as LazyQueryResult<D, OperationVariables>).networkStatus)
        : (result as MutationResult<D>).loading
}

type ApolloStateButtonProps<D> = ApolloStateButtonAttributes<HTMLButtonElement, D> & { variant?: ButtonVariant; size?: ButtonSize }

export function ApolloStateButton<D>({ result, children, variant, size, ...rest }: ApolloStateButtonProps<D>): JSX.Element {
    return (
        <Button variant={variant} size={size} {...rest}>
            {resultLoading<D>(result) ? <SpinnerIcon /> : children}
        </Button>
    )
}

export function ApolloStatePrimaryButton<D>(props: ApolloStateButtonProps<D>): JSX.Element {
    return <ApolloStateButton<D> variant='primary' {...props} />
}
